import { useServicesContext } from "@modules/core/services/services.context"
import { SharedCoachRequestAction } from "@modules/programs/types/programTypes"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const useSharedCoachAccept = () => {
  const { sharedCoachApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: acceptSharedCoach, isPending } = useMutation({
    mutationFn: async (programId: string) => {
      await sharedCoachApi.acceptSharedCoach(programId)
    },
    onSuccess: (_data, programId) => {
      queryClient.invalidateQueries({
        queryKey: ["program", programId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programs"],
      })
    },
  })

  return { acceptSharedCoach, isPending }
}

export const useSharedCoachRefuse = () => {
  const { sharedCoachApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: refuseSharedCoach, isPending } = useMutation({
    mutationFn: async (programId: string) => {
      await sharedCoachApi.refuseSharedCoach(programId)
    },
    onSuccess: (_data, programId) => {
      queryClient.invalidateQueries({
        queryKey: ["program", programId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programs"],
      })
    },
  })

  return { refuseSharedCoach, isPending }
}

export const useSharedCoachQuit = () => {
  const { sharedCoachApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: quitSharedCoach, isPending } = useMutation({
    mutationFn: async (programId: string) => {
      await sharedCoachApi.quitSharedCoach(programId)
    },
    onSuccess: (_data, programId) => {
      queryClient.invalidateQueries({
        queryKey: ["program", programId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programs"],
      })
    },
  })

  return { quitSharedCoach, isPending }
}

export const useSharedCoachRemove = () => {
  const { sharedCoachApi } = useServicesContext()
  const queryClient = useQueryClient()
  const { mutateAsync: removeSharedCoach, isPending } = useMutation({
    mutationFn: async ({ programId, coachId }: { programId: string; coachId: string }) => {
      await sharedCoachApi.removeSharedCoach(programId, coachId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["program", variables.programId],
      })
    },
  })

  return { removeSharedCoach, isPending }
}

export const useSharedCoachRequest = () => {
  const { removeSharedCoach, isPending: isRemoveSharedCoachPending } = useSharedCoachRemove()
  const { acceptSharedCoach, isPending: isAcceptSharedCoachPending } = useSharedCoachAccept()
  const { refuseSharedCoach, isPending: isRefuseSharedCoachPending } = useSharedCoachRefuse()
  const { quitSharedCoach, isPending: isQuitSharedCoachPending } = useSharedCoachQuit()

  const isPending =
    isRemoveSharedCoachPending || isAcceptSharedCoachPending || isRefuseSharedCoachPending || isQuitSharedCoachPending

  const requestSharedCoach = async (programId: string, action: SharedCoachRequestAction, coachId: string) => {
    switch (action) {
      case SharedCoachRequestAction.ACCEPT:
        await acceptSharedCoach(programId)
        break
      case SharedCoachRequestAction.REFUSE:
        await refuseSharedCoach(programId)
        break
      case SharedCoachRequestAction.QUIT:
        await quitSharedCoach(programId)
        break
      case SharedCoachRequestAction.REMOVE:
        await removeSharedCoach({ programId, coachId })
        break
    }
  }

  return { isPending, requestSharedCoach }
}
